import { html } from '../core/html.js';
import { t } from '../core/i18n.js';
import { page, breadcrumbs } from '../components/chrome.js';
import { legalCta, legalSection, legalToc, legalTranslationNotice } from '../components/legal.js';

const sections = [
  { id: 'controller', titleKey: 'privacy.controller.title' },
  { id: 'general', titleKey: 'privacy.general.title' },
  { id: 'collection', titleKey: 'privacy.collection.title' },
  { id: 'legal-basis', titleKey: 'privacy.legal.title' },
  { id: 'cookies', titleKey: 'privacy.cookies.title' },
  { id: 'apps', titleKey: 'privacy.apps.title' },
  { id: 'rights', titleKey: 'privacy.rights.title' },
  { id: 'complaint', titleKey: 'privacy.complaint.title' },
  { id: 'security', titleKey: 'privacy.security.title' },
  { id: 'changes', titleKey: 'privacy.changes.title' },
];

export const PrivacyPage = () => page(html`
  <section class="pagehead pagehead--legal" aria-labelledby="privacy-title">
    <div class="shell">
      ${breadcrumbs('nav.privacy')}
      <div class="pagehead__grid">
        <div>
          <p class="kicker">${t('privacy.page.eyebrow')}</p>
          <h1 class="pagehead__title" id="privacy-title">${t('privacy.title')}</h1>
          <p class="pagehead__lede">${t('privacy.page.description')}</p>
        </div>
        <aside class="pagehead__aside">
          <p class="kicker">${t('privacy.dataprotection.title')}</p>
          <p class="pagehead__note">
            <strong>${t('privacy.controller.company')}</strong><br />
            ${t('privacy.dataprotection.updated')}<br />
            ${t('privacy.controller.email')}:
            <a href="mailto:info@netbyte.bz">info@netbyte.bz</a>
          </p>
        </aside>
      </div>
    </div>
  </section>

  <section class="section legal">
    <div class="shell legal__layout">
      ${legalToc({
        titleKey: 'privacy.summary.title',
        items: sections,
        meta: t('privacy.updated'),
      })}
      <div class="legal__doc">
        ${legalTranslationNotice()}
        ${legalSection({
          id: 'controller',
          iconName: 'check',
          titleKey: 'privacy.controller.title',
          body: html`
            <p><strong>${t('privacy.controller.company')}</strong></p>
            <p>${t('privacy.controller.owner')}: ${t('privacy.controller.owner.name')}</p>
            <p>
              ${t('privacy.controller.address.street')}<br />
              ${t('privacy.controller.address.city')}<br />
              ${t('privacy.controller.address.country')}
            </p>
            <p>
              <strong>${t('privacy.controller.email')}</strong>:
              <a href="mailto:info@netbyte.bz">info@netbyte.bz</a>
            </p>
          `,
        })}

        ${legalSection({
          id: 'general',
          iconName: 'note',
          titleKey: 'privacy.general.title',
          body: html`
            <p>${t('privacy.general.content1')}</p>
            <p>${t('privacy.general.content2')}</p>
          `,
        })}

        ${legalSection({
          id: 'collection',
          iconName: 'code',
          titleKey: 'privacy.collection.title',
          body: html`
            <p>${t('privacy.collection.intro')}</p>
            <p><strong>${t('privacy.collection.data')}</strong></p>
            <ul>
              <li>${t('privacy.collection.ip')}</li>
              <li>${t('privacy.collection.datetime')}</li>
              <li>${t('privacy.collection.timezone')}</li>
              <li>${t('privacy.collection.content')}</li>
              <li>${t('privacy.collection.status')}</li>
              <li>${t('privacy.collection.volume')}</li>
              <li>${t('privacy.collection.referrer')}</li>
              <li>${t('privacy.collection.browser')}</li>
              <li>${t('privacy.collection.os')}</li>
              <li>${t('privacy.collection.language')}</li>
            </ul>
          `,
        })}

        ${legalSection({
          id: 'legal-basis',
          iconName: 'check',
          titleKey: 'privacy.legal.title',
          body: html`<p>${t('privacy.legal.content')}</p>`,
        })}

        ${legalSection({
          id: 'cookies',
          iconName: 'code',
          titleKey: 'privacy.cookies.title',
          body: html`
            <p>${t('privacy.cookies.content')}</p>
            <p>${t('privacy.cookies.settings')}</p>
          `,
        })}

        ${legalSection({
          id: 'apps',
          iconName: 'phone',
          titleKey: 'privacy.apps.title',
          body: html`
            <p>${t('privacy.apps.intro')}</p>
            <h3 class="legal__sub">${t('privacy.apps.admob.title')}</h3>
            <p>${t('privacy.apps.admob.content')}</p>
            <h3 class="legal__sub">${t('privacy.apps.adid.title')}</h3>
            <p>${t('privacy.apps.adid.content')}</p>
            <h3 class="legal__sub">${t('privacy.apps.revenuecat.title')}</h3>
            <p>${t('privacy.apps.revenuecat.content')}</p>
            <h3 class="legal__sub">${t('privacy.apps.storage.title')}</h3>
            <p>${t('privacy.apps.storage.content')}</p>
            <h3 class="legal__sub">${t('privacy.apps.notifications.title')}</h3>
            <p>${t('privacy.apps.notifications.content')}</p>
          `,
        })}

        ${legalSection({
          id: 'rights',
          iconName: 'check',
          titleKey: 'privacy.rights.title',
          body: html`
            <p><strong>${t('privacy.rights.intro')}</strong></p>
            <ul>
              <li><strong>${t('privacy.rights.info')}</strong></li>
              <li><strong>${t('privacy.rights.rectification')}</strong></li>
              <li><strong>${t('privacy.rights.erasure')}</strong></li>
              <li><strong>${t('privacy.rights.restriction')}</strong></li>
              <li><strong>${t('privacy.rights.portability')}</strong></li>
              <li><strong>${t('privacy.rights.object')}</strong></li>
            </ul>
            <p>${t('privacy.rights.contact')}</p>
          `,
        })}

        ${legalSection({
          id: 'complaint',
          iconName: 'check',
          titleKey: 'privacy.complaint.title',
          body: html`
            <p>${t('privacy.complaint.content')}</p>
            <p><strong>${t('privacy.complaint.authority')}</strong></p>
            <p>
              ${t('privacy.complaint.authority.name')}<br />
              Lennéstraße 1<br />
              19053 Schwerin
            </p>
          `,
        })}

        ${legalSection({
          id: 'security',
          iconName: 'check',
          titleKey: 'privacy.security.title',
          body: html`<p>${t('privacy.security.content')}</p>`,
        })}

        ${legalSection({
          id: 'changes',
          iconName: 'note',
          titleKey: 'privacy.changes.title',
          body: html`
            <p>${t('privacy.changes.content')}</p>
            <p class="legal__updated">${t('privacy.updated')}</p>
          `,
        })}

        ${legalCta()}
      </div>
    </div>
  </section>
`, { modifier: 'main--legal' });
